/**
 * PinterestAdMockup Component
 *
 * Pinterest Pin ad mockup
 * Vertical format (9:16 or 2:3 aspect ratio)
 */

import React from 'react';
import { stripMediaTags } from '@/utils/mediaTagParser';
import { AdMockupContainer } from '../base/AdMockupContainer';
import { AdMockupMedia } from '../base/AdMockupMedia';
import { AdMockupCTA } from '../base/AdMockupCTA';

export interface PinterestAdMockupProps {
  imageUrl?: string;
  videoUrl?: string;
  title: string;
  description?: string;
  callToAction?: string;
  brandName?: string;
  boardName?: string;
  linkUrl?: string;
}

export const PinterestAdMockup: React.FC<PinterestAdMockupProps> = ({
  imageUrl,
  videoUrl,
  title,
  description,
  callToAction = 'Learn More',
  brandName = 'Your Brand',
  boardName = 'Saved to Board',
  linkUrl,
}) => {
  const mediaUrl = videoUrl || imageUrl;
  const displayDescription = stripMediaTags(description || '');

  return (
    <AdMockupContainer platform="pinterest">
      <div className="rounded-2xl border-2 border-gray-200 dark:border-border-dark overflow-hidden bg-white dark:bg-card-darkAlt shadow-md max-w-sm mx-auto">
        {/* Pinterest Pin Card */}
        <div className="relative">
          {/* Pin Image/Video */}
          {mediaUrl ? (
            <AdMockupMedia
              mediaUrl={mediaUrl}
              aspectRatio="9:16"
              platform="pinterest"
              alt={title}
              showPlayButton={!!videoUrl}
            />
          ) : (
            <div className="w-full bg-gradient-to-br from-pink-100 via-red-50 to-orange-100 dark:from-gray-800 dark:via-gray-700 dark:to-gray-600 flex items-center justify-center" style={{ aspectRatio: '9/16' }}>
              <svg className="w-20 h-20 text-pinterest opacity-30" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
              </svg>
            </div>
          )}

          {/* "Promoted" Badge */}
          <div className="absolute top-3 left-3">
            <span className="px-2 py-1 bg-white/90 dark:bg-card-darkAlt/90 backdrop-blur-sm text-xs font-semibold text-gray-700 dark:text-gray-300 rounded-full shadow-sm">
              Promoted
            </span>
          </div>

          {/* Save Button (top right) */}
          <div className="absolute top-3 right-3">
            <button className="w-10 h-10 rounded-full bg-pinterest hover:bg-pinterest-dark transition-colors flex items-center justify-center shadow-lg">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Pin Info Section */}
        <div className="p-4">
          {/* Title */}
          <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
            {title}
          </h3>

          {/* Description */}
          {displayDescription && (
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-3">
              {displayDescription}
            </p>
          )}

          {/* Link/CTA */}
          {linkUrl && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-3">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <span className="truncate">{new URL(linkUrl || 'https://example.com').hostname}</span>
            </div>
          )}

          {/* CTA Button */}
          {callToAction && (
            <AdMockupCTA
              text={callToAction}
              platform="pinterest"
              variant="primary"
              fullWidth={true}
            />
          )}

          {/* Brand Info */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-border-dark">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pinterest to-red-700 flex items-center justify-center text-white text-xs font-bold">
                {brandName.slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {brandName}
                </p>
                {boardName && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    Saved to {boardName}
                  </p>
                )}
              </div>
              <button className="px-3 py-1 bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm font-semibold text-gray-700 dark:text-gray-300 rounded-full transition-colors">
                Follow
              </button>
            </div>
          </div>
        </div>

        {/* Engagement Bar */}
        <div className="px-4 pb-4 flex items-center gap-4 text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
            <span className="text-sm font-medium">3.2K</span>
          </div>
          <div className="flex items-center gap-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span className="text-sm font-medium">127</span>
          </div>
          <div className="flex items-center gap-1 ml-auto">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            <span className="text-sm font-medium">892</span>
          </div>
        </div>
      </div>

      {/* Helper Text */}
      <p className="mt-3 text-xs text-center text-gray-500 dark:text-gray-400">
        📌 Pinterest Pin Preview • Vertical format (9:16)
      </p>
    </AdMockupContainer>
  );
};
